import { useCallback, useEffect, useRef, useState } from "react"

// Links in the preview open a new tab instead of navigating the frame.
function withBlankTargets(html: string) {
	return html.includes("<head>")
		? html.replace("<head>", '<head><base target="_blank">')
		: `<base target="_blank">${html}`
}

/**
 * The exact email HTML the broadcast sends, in a sandbox with scripts off. Same
 * origin is allowed only so the frame can grow to the email's height.
 */
export function EmailPreviewFrame({ html }: { html: string }) {
	const frameRef = useRef<HTMLIFrameElement>(null)
	const observerRef = useRef<ResizeObserver | null>(null)
	const [height, setHeight] = useState(480)

	// Each srcdoc swap loads a new document, so the observer follows every load.
	const handleLoad = useCallback(() => {
		observerRef.current?.disconnect()
		const root = frameRef.current?.contentDocument?.documentElement
		if (!root) return
		setHeight(root.scrollHeight)
		// Images finish after load and change the email's height.
		const observer = new ResizeObserver(() => setHeight(root.scrollHeight))
		observer.observe(root)
		observerRef.current = observer
	}, [])

	useEffect(() => () => observerRef.current?.disconnect(), [])

	return (
		<iframe
			ref={frameRef}
			title="Email preview"
			srcDoc={withBlankTargets(html)}
			sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
			onLoad={handleLoad}
			style={{ height }}
			className="block w-full border-0 bg-transparent"
		/>
	)
}
